import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  appointmentsApi,
} from "@/api/appointments/appointments";
import { invalidateAppointmentQueries } from "@/lib/appointments/invalidate-appointment-queries";
import {
  CLIENT_UPCOMING_PAGE_SIZE,
  CLIENT_UPCOMING_STATUSES,
} from "@/constants/clients/client-appointment-constants";
import type {
  AppointmentCreate,
  AppointmentUpdate,
} from "@/types/appointments/api-types/appointment-types";
import { toIsoDate } from "@/lib/utils";

export const useGetAppointments = (date: Date) => {
  const appointment_date = toIsoDate(date);
  return useQuery({
    queryKey: ["appointments", { appointment_date }],
    queryFn: () =>
      appointmentsApi.getAll({ appointment_date, size: 100 }),
  });
};

export const useGetAvailability = (
  date: Date,
  excludeAppointmentId?: number
) => {
  const dateStr = toIsoDate(date);
  const queryKey =
    excludeAppointmentId === undefined
      ? (["appointments-availability", { date: dateStr }] as const)
      : ([
          "appointments-availability",
          { date: dateStr, exclude_appointment_id: excludeAppointmentId },
        ] as const);

  return useQuery({
    queryKey,
    queryFn: () =>
      appointmentsApi.getAvailability(
        excludeAppointmentId === undefined
          ? { date: dateStr }
          : {
              date: dateStr,
              exclude_appointment_id: excludeAppointmentId,
            }
      ),
    placeholderData: keepPreviousData,
  });
};

export const useCreateAppointment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: AppointmentCreate) => appointmentsApi.create(data),
    onSuccess: () => invalidateAppointmentQueries(queryClient),
  });
};

export const useGetClientUpcomingAppointments = (
  clientId: number | undefined,
  enabled: boolean
) =>
  useQueries({
    queries: CLIENT_UPCOMING_STATUSES.map((status) => ({
      queryKey: [
        "appointments",
        { page: 0, size: CLIENT_UPCOMING_PAGE_SIZE, client_id: clientId, status },
      ],
      queryFn: () => {
        if (clientId == null) {
          throw new Error("client_id is required");
        }
        return appointmentsApi.getAll({
          page: 0,
          size: CLIENT_UPCOMING_PAGE_SIZE,
          client_id: clientId,
          status,
        });
      },
      enabled: Boolean(enabled && clientId != null),
    })),
    combine: (results) => ({
      items: results.flatMap((result) => result.data?.items ?? []),
      isPending: results.some((result) => result.isPending),
      isError: results.some((result) => result.isError),
      isSuccess: results.every((result) => result.isSuccess),
    }),
  });

export const useUpdateAppointment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: AppointmentUpdate }) =>
      appointmentsApi.update(id, data),
    onSuccess: () => invalidateAppointmentQueries(queryClient),
  });
};
